import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { STATES } from "@/lib/states";
import { Plus, Home, MapPin, ArrowRight, X, Calendar as CalIcon } from "lucide-react";
import TenancyProgress from "@/components/TenancyProgress";
import WhatsNewBanner from "@/components/WhatsNewBanner";
import WelcomeBanner from "@/components/WelcomeBanner";
import UnverifiedEmailBanner from "@/components/UnverifiedEmailBanner";
import BoostBanner from "@/components/BoostBanner";
import { UpgradeNudge } from "@/components/UpgradeNudge";
import EmptyState from "@/components/EmptyState";
import { SkeletonCard } from "@/components/Skeleton";
import { colorForCase } from "@/lib/caseColor";

function NewCaseModal({ open, onClose, onCreated }) {
 const [form, setForm] = useState({
 title: "", address: "", state: "QLD",
 landlord_name: "", agent_name: "", agency: "", agent_email: "",
 lease_start: "", lease_end: "", bond_amount: "", weekly_rent: "", notes: ""
 });
 const [busy, setBusy] = useState(false);

 if (!open) return null;

 const submit = async (e) => {
 e.preventDefault();
 setBusy(true);
 try {
 const payload = {...form };
 payload.bond_amount = payload.bond_amount ? Number(payload.bond_amount) : null;
 payload.weekly_rent = payload.weekly_rent ? Number(payload.weekly_rent) : null;
 const { data } = await api.post("/cases", payload);
 onCreated(data);
 } catch (e) {
 alert(e.response?.data?.detail || "Could not create case");
 } finally { setBusy(false); }
 };

 return (
 <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
 <div className="bg-[#FAF9F6] border border-[#E5E4DE] rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-case-modal">
 <div className="flex justify-between items-center p-6 border-b border-[#E5E4DE]">
 <h3 className="font-serif text-2xl">New Case</h3>
 <button onClick={onClose} className="btn-ghost p-1.5" data-testid="close-modal"><X size={18} strokeWidth={1.5} /></button>
 </div>
 <form onSubmit={submit} className="p-6 space-y-4">
 <div className="grid sm:grid-cols-2 gap-4">
 <div><label className="label-mono block mb-1.5">Case title</label>
 <input required className="input-field" placeholder="My Brisbane unit" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} data-testid="case-title" /></div>
 <div><label className="label-mono block mb-1.5">State / Territory</label>
 <select className="input-field" value={form.state} onChange={e=>setForm({...form,state:e.target.value})} data-testid="case-state">
 {STATES.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
 </select></div>
 </div>
 <div><label className="label-mono block mb-1.5">Property address</label>
 <input required className="input-field" placeholder="12 Marlow Cres, Brisbane QLD 4000" value={form.address} onChange={e=>setForm({...form,address:e.target.value})} data-testid="case-address" /></div>
 <div className="grid sm:grid-cols-3 gap-4">
 <div><label className="label-mono block mb-1.5">Landlord</label><input className="input-field" value={form.landlord_name} onChange={e=>setForm({...form,landlord_name:e.target.value})} data-testid="case-landlord" /></div>
 <div><label className="label-mono block mb-1.5">Agent</label><input className="input-field" value={form.agent_name} onChange={e=>setForm({...form,agent_name:e.target.value})} data-testid="case-agent" /></div>
 <div><label className="label-mono block mb-1.5">Agency</label><input className="input-field" value={form.agency} onChange={e=>setForm({...form,agency:e.target.value})} data-testid="case-agency" /></div>
 </div>
 <div><label className="label-mono block mb-1.5">Agent / landlord email <span className="text-[#9CA3AF] font-mono text-[10px] normal-case">(used to pre-fill letter sends)</span></label><input type="email" className="input-field" placeholder="agent@example.com" value={form.agent_email} onChange={e=>setForm({...form,agent_email:e.target.value})} data-testid="case-agent-email" /></div>
 <div className="grid sm:grid-cols-2 gap-4">
 <div><label className="label-mono block mb-1.5">Lease start</label><input type="date" className="input-field" value={form.lease_start} onChange={e=>setForm({...form,lease_start:e.target.value})} data-testid="case-lease-start" /></div>
 <div><label className="label-mono block mb-1.5">Lease end</label><input type="date" className="input-field" value={form.lease_end} onChange={e=>setForm({...form,lease_end:e.target.value})} data-testid="case-lease-end" /></div>
 </div>
 <div className="grid sm:grid-cols-2 gap-4">
 <div><label className="label-mono block mb-1.5">Bond ($)</label><input type="number" className="input-field" value={form.bond_amount} onChange={e=>setForm({...form,bond_amount:e.target.value})} data-testid="case-bond" /></div>
 <div><label className="label-mono block mb-1.5">Weekly rent ($)</label><input type="number" className="input-field" value={form.weekly_rent} onChange={e=>setForm({...form,weekly_rent:e.target.value})} data-testid="case-rent" /></div>
 </div>
 <div><label className="label-mono block mb-1.5">Notes</label>
 <textarea className="input-field" rows={3} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} data-testid="case-notes" /></div>
 <div className="flex justify-end gap-3 pt-2">
 <button type="button" onClick={onClose} className="btn-secondary" data-testid="cancel-case">Cancel</button>
 <button type="submit" disabled={busy} className="btn-primary" data-testid="submit-case">{busy ? "Creating…" : "Create case"}</button>
 </div>
 </form>
 </div>
 </div>
 );
}

export default function Dashboard() {
 const { user } = useAuth();
 const [cases, setCases] = useState([]);
 const [loading, setLoading] = useState(true);
 const [modalOpen, setModalOpen] = useState(false);
 const navigate = useNavigate();
 const [searchParams, setSearchParams] = useSearchParams();

 useEffect(() => {
 api.get("/cases").then(({ data }) => setCases(data)).finally(() => setLoading(false));
 }, []);

 // Auto-open the new-case modal when arrived via /dashboard?new=1
 // (used by the safety redirect from /cases/new and by the welcome banner
 // fallback). Clears the query param so a refresh does not re-open the modal.
 useEffect(() => {
 if (searchParams.get("new") === "1") {
 setModalOpen(true);
 const sp = new URLSearchParams(searchParams);
 sp.delete("new");
 setSearchParams(sp, { replace: true });
 }
 }, [searchParams, setSearchParams]);

 const handleCreated = (c) => {
 setCases([c,...cases]);
 setModalOpen(false);
 navigate(`/cases/${c.id}`);
 };

 return (
 <div className="min-h-screen paper-grain">
 <Navbar />
 <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
 <UnverifiedEmailBanner />
 <BoostBanner />
 <WhatsNewBanner />
 <WelcomeBanner caseCount={cases.length} onStart={() => setModalOpen(true)} />
 <UpgradeNudge variant="quota" className="mb-6" />
 <div className="flex justify-between items-end mb-10">
 <div>
 <div className="label-mono mb-2">Hello, {user?.name}</div>
 <h1 className="font-serif text-4xl text-[#1A1A1A]">Your cases</h1>
 </div>
 <div className="flex items-center gap-3">
 {cases.length >= 2 && (
 <Link to="/calendar" className="btn-secondary inline-flex items-center gap-2" data-testid="cross-case-calendar-link">
 <CalIcon size={16} strokeWidth={1.5} /> Calendar
 </Link>
 )}
 <button onClick={() => setModalOpen(true)} className="btn-primary inline-flex items-center gap-2" data-testid="new-case-btn">
 <Plus size={16} strokeWidth={1.5} /> New case
 </button>
 </div>
 </div>

 {loading ? (
 <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
 <SkeletonCard /><SkeletonCard /><SkeletonCard />
 </div>
 ) : cases.length === 0 ? (
 <EmptyState
 icon={Home}
 title="Your first tenancy case starts here"
 description="A case is a folder for one rental: your lease, photos, emails, condition reports and the AI that ties them together. Most users finish in under 10 minutes."
 actionLabel="Create your first case"
 onAction={() => setModalOpen(true)}
 testid="empty-state"
 secondary="No card needed. You stay on the Free plan until you choose to upgrade."
 />
 ) : (
 <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
 {cases.map((c) => {
 const col = colorForCase(c.id);
 return (
 <Link
 key={c.id}
 to={`/cases/${c.id}`}
 className="card card-hover block"
 style={cases.length >= 2 ? { borderLeft: `3px solid ${col.stripe}` } : undefined}
 data-testid={`case-card-${c.id}`}
 >
 <div className="label-mono mb-2 flex items-center justify-between">
 <span className="inline-flex items-center gap-1.5">
 {cases.length >= 2 && (
 <span
 className="w-2 h-2 inline-block"
 style={{ background: col.stripe }}
 aria-hidden="true"
 data-testid={`case-color-dot-${c.id}`}
 />
 )}
 {c.state}
 </span>
 <ArrowRight size={14} strokeWidth={1.5} className="text-[#6B7280]" />
 </div>
 <h3 className="font-serif text-xl text-[#1A1A1A] mb-1 leading-snug">{c.title}</h3>
 <div className="text-sm text-[#6B7280] flex items-start gap-1.5 mb-4">
 <MapPin size={14} strokeWidth={1.5} className="mt-0.5 flex-shrink-0" />
 <span className="leading-snug">{c.address}</span>
 </div>
 <div className="border-t border-[#E5E4DE] pt-3 grid grid-cols-2 gap-2 text-xs text-[#6B7280]">
 <div><span className="text-[#1A1A1A] font-mono">${c.weekly_rent || "–"}</span>/wk</div>
 <div className="text-right">Bond <span className="text-[#1A1A1A] font-mono">${c.bond_amount || "–"}</span></div>
 </div>
 <TenancyProgress leaseStart={c.lease_start} leaseEnd={c.lease_end} size="compact" />
 </Link>
 );
 })}
 </div>
 )}
 </div>

 <NewCaseModal open={modalOpen} onClose={() => setModalOpen(false)} onCreated={handleCreated} />
 </div>
 );
}
